import type { LocatorDestinationCode } from '../../../../../core/api/locator/models/locator.model';
import type { PickupTransportationMode } from '../../../../../core/api/pickup/models/pickup.model';

/** Inputs for resolving pickup/locator route codes from Service-step state. */
export interface ResolvePickupRouteCodesInput {
  /** PSA transportation mode from the selected delivery offering (`A` | `G`). */
  transportationMode?: string | null;
  /** Ship To country code from the address form (e.g. `CA`, `US`). */
  shipToCountry?: string | null;
}

/** Resolved codes for locator search, pickup validate, and schedule. */
export interface PickupRouteCodes {
  transportationMode: PickupTransportationMode;
  destinationCode: LocatorDestinationCode;
}

/**
 * Maps PSA product transportation mode (`A`/`G`) to the pickup/locator API enum.
 */
export function mapPickupTransportationMode(
  psaMode: string | null | undefined,
): PickupTransportationMode {
  const normalized = (psaMode ?? '').trim().toUpperCase();
  if (normalized === 'A') {
    return 'AIR';
  }
  return 'GROUND';
}

/**
 * Maps Ship To country to a pickup/locator destination code.
 * OLS address forms are Canada-first today (no country field), so missing country
 * defaults to `DOM`. Locator wire maps `INTL` → `INT` at the API client boundary.
 */
export function mapPickupDestinationCode(
  shipToCountry: string | null | undefined,
): LocatorDestinationCode {
  const normalized = (shipToCountry ?? '').trim().toUpperCase();
  if (!normalized || normalized === 'CA') {
    return 'DOM';
  }
  if (normalized === 'US') {
    return 'USA';
  }
  return 'INTL';
}

/**
 * Resolves transportation and destination codes for pickup/locator calls from
 * the selected delivery service and Ship To country.
 */
export function resolvePickupRouteCodes(input: ResolvePickupRouteCodesInput): PickupRouteCodes {
  return {
    transportationMode: mapPickupTransportationMode(input.transportationMode),
    destinationCode: mapPickupDestinationCode(input.shipToCountry),
  };
}
